import { Link } from 'react-router-dom';
import usePageMeta from '../lib/usePageMeta';
import PageHero from '../components/PageHero';
import Icon from '../components/Icon';
import { SectionHead } from '../components/Blocks';
import { LIFE_EVENTS, LIFE_PERKS } from '../lifeData';
import { IMG, u } from '../images';

// Photo with graceful fallback: drop a file at the `photo` path (inside /public) and it replaces the placeholder tile.
function EventVisual({ ev, index }) {
  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-gradient-to-br from-brand to-ink shadow-xl">
      <img src={ev.photo} alt={ev.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-brand/10" />
      <span className="absolute left-5 top-5 rounded-full bg-brand-lime px-3 py-1 text-xs font-semibold text-ink">{ev.tag}</span>
      <span className="absolute bottom-3 right-5 font-display text-6xl font-bold text-white/30">{String(index + 1).padStart(2, '0')}</span>
      <span className="absolute bottom-5 left-5 grid h-12 w-12 place-items-center rounded-2xl bg-ink/60 text-brand-lime backdrop-blur"><Icon name={ev.icon} className="h-6 w-6" /></span>
    </div>
  );
}

export default function Life() {
  usePageMeta('Life at Origo', 'See life at Origo Private Limited: annual picnics, birthday celebrations, Ramazan iftar buffets, Eid, sports days and a team that genuinely enjoys working together.');

  return (
    <>
      <PageHero
        eyebrow="Culture & people"
        title="Life at Origo"
        description="Great work comes from people who enjoy working together. Here is a look at the celebrations, traditions and everyday moments that make our team feel like family."
        crumbs={[{ label: 'Life at Origo' }]}
      >
        <Link to="/contact" className="btn-primary">Join our team <Icon name="arrow" className="h-4 w-4" /></Link>
      </PageHero>

      <section className="section bg-white">
        <div className="container-x">
          <SectionHead eyebrow="What we value" title="A workplace people look forward to" text="We celebrate wins, mark milestones and make time for each other, because culture is built in moments outside the project plan too." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-anim="up" data-stagger>
            {LIFE_PERKS.map((p) => (
              <div key={p.title} className="lift rounded-3xl border border-brand/10 bg-brand-soft/60 p-7">
                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand text-white"><Icon name={p.icon} className="h-7 w-7" /></span>
                <h3 className="mt-5 text-lg font-bold text-ink">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {LIFE_EVENTS.map((ev, i) => (
        <section key={ev.slug} id={ev.slug} className={`section ${i % 2 === 0 ? 'bg-brand-soft' : 'bg-white'}`}>
          <div className={`container-x grid items-center gap-12 lg:grid-cols-2 lg:gap-16 ${i % 2 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
            <div data-anim={i % 2 ? 'right' : 'left'}><EventVisual ev={{ ...ev, photo: u(IMG.life[ev.slug], 1000) }} index={i} /></div>
            <div data-anim={i % 2 ? 'left' : 'right'}>
              <span className="eyebrow">{ev.tag}</span>
              <h2 className="h2 mt-5 text-ink">{ev.title}</h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-600">{ev.text}</p>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {ev.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand text-white"><Icon name="check" className="h-3 w-3" strokeWidth={3} /></span>{pt}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ))}

      <section className="section bg-ink text-white">
        <div className="container-x relative text-center">
          <span className="eyebrow eyebrow-dark">Careers</span>
          <h2 className="h2 mx-auto mt-5 max-w-2xl" data-split>Want to be part of the <span className="text-accent">Origo</span> <span className="text-outline">family</span>?</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-white/65" data-anim="up">We are always happy to meet curious, talented people. Send us a note and tell us what you would love to build.</p>
          <div className="mt-8" data-anim="up"><Link to="/contact" className="btn-primary">Get in touch <Icon name="arrow" className="h-4 w-4" /></Link></div>
        </div>
      </section>
    </>
  );
}
